import React, { useState, useEffect, useCallback } from 'react';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_WHATSAPP_API || 'https://whatsapp.socialpartners.in/api';

/* ── Helpers ────────────────────────────────────────────────────────────── */
const fmtTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(parseInt(ts) * 1000);
  return isNaN(d) ? '—' : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = new Date(parseInt(ts) * 1000);
  return isNaN(d) ? '—' : d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};
const extractTemplateName = (body) => {
  if (!body) return 'Unknown';
  const m = body.match(/^Template:\s*(.+?)\s*(\||$)/);
  return m ? m[1] : body.slice(0, 30);
};
const pct = (val, total) => total > 0 ? Math.round((val / total) * 100) : 0;

/* ── Status config ──────────────────────────────────────────────────────── */
const STATUS = {
  sent:      { color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', label: 'Sent',      icon: 'fa-paper-plane' },
  delivered: { color: '#10b981', bg: '#f0fdf4', border: '#a7f3d0', label: 'Delivered', icon: 'fa-check-double' },
  read:      { color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe', label: 'Read',      icon: 'fa-eye' },
  failed:    { color: '#ef4444', bg: '#fef2f2', border: '#fecaca', label: 'Failed',    icon: 'fa-times-circle' },
};
const getStatus = (s) => STATUS[s] || { color: '#94a3b8', bg: '#f8fafc', border: '#e2e8f0', label: s || 'Unknown', icon: 'fa-circle' };

/* ── Badge ──────────────────────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const s = getStatus(status);
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: 20, padding: '3px 12px',
      fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5,
    }}>
      <i className={`fas ${s.icon}`} style={{ fontSize: 10 }}></i>
      {s.label}
    </span>
  );
}

/* ── Stat card ──────────────────────────────────────────────────────────── */
function StatCard({ label, value, color, bg, border, icon, sub, pctVal }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 16, padding: '20px 24px',
      border: `1px solid #e2e8f0`, flex: 1, minWidth: 160,
      boxShadow: '0 1px 4px rgba(0,0,0,.05)',
      borderTop: `4px solid ${color}`,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', right: 16, top: 16,
        width: 44, height: 44, borderRadius: 12,
        background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <i className={`fas ${icon}`} style={{ fontSize: 18, color }}></i>
      </div>
      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: 34, fontWeight: 800, color: '#0f172a', letterSpacing: -1, lineHeight: 1 }}>
        {Number(value).toLocaleString()}
      </div>
      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        {pctVal !== undefined && (
          <span style={{
            background: color + '18', color, borderRadius: 20,
            padding: '2px 8px', fontSize: 12, fontWeight: 700,
          }}>{pctVal}%</span>
        )}
        {sub && <span style={{ fontSize: 12, color: '#94a3b8' }}>{sub}</span>}
      </div>
    </div>
  );
}

/* ── Progress bar ───────────────────────────────────────────────────────── */
function ProgressBar({ value, max, color }) {
  const p = pct(value, max);
  return (
    <div style={{ height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${p}%`, background: color,
        borderRadius: 99, transition: 'width .7s ease',
      }} />
    </div>
  );
}

/* ── Delivery funnel ────────────────────────────────────────────────────── */
function FunnelRow({ label, value, total, color, icon }) {
  const p = pct(value, total);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <i className={`fas ${icon}`} style={{ fontSize: 14, color }}></i>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{label}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color }}>{value.toLocaleString()}</span>
            <span style={{
              background: color + '15', color, borderRadius: 20,
              padding: '1px 8px', fontSize: 12, fontWeight: 600, minWidth: 42, textAlign: 'center',
            }}>{p}%</span>
          </div>
        </div>
        <ProgressBar value={value} max={total} color={color} />
      </div>
    </div>
  );
}

/* ── Main ───────────────────────────────────────────────────────────────── */
export default function BroadcastReport() {
  const [messages, setMessages]           = useState([]);
  const [waContacts, setWaContacts]       = useState({});
  const [loading, setLoading]             = useState(true);
  const [retrying, setRetrying]           = useState(false);
  const [error, setError]                 = useState('');
  const [activeTab, setActiveTab]         = useState('history');
  const [filterStatus, setFilterStatus]   = useState('all');
  const [dateFrom, setDateFrom]           = useState('');
  const [dateTo, setDateTo]               = useState('');
  const [searchName, setSearchName]       = useState('');
  const [expandedGroup, setExpandedGroup] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      setLoading(true); setError('');
      const [msgRes, contactRes] = await Promise.allSettled([
        fetch(`${API}/broadcast/history`),
        fetch(`${API}/wa-contacts`),
      ]);

      let msgs = [];
      if (msgRes.status === 'fulfilled' && msgRes.value.ok) {
        const d = await msgRes.value.json();
        msgs = d.data || d || [];
      } else {
        setError('Fallback mode — /broadcast/history not found. Add it to your backend for full accuracy.');
        const convRes = await fetch(`${API}/conversations`);
        const convData = await convRes.json();
        const convList = Array.isArray(convData) ? convData
          : Array.isArray(convData.data) ? convData.data
          : Array.isArray(convData.conversations) ? convData.conversations : [];
        const all = [];
        await Promise.allSettled(convList.slice(0, 100).map(async conv => {
          try {
            const r = await fetch(`${API}/chat/${conv.wa_id}`);
            const d = await r.json();
            const list = Array.isArray(d) ? d : Array.isArray(d.data) ? d.data : [];
            list.filter(m => m.direction === 'outbound' && m.type === 'template')
              .forEach(m => all.push({ ...m, _contactName: conv.profile_name || conv.wa_id, _wa_id: conv.wa_id }));
          } catch {}
        }));
        msgs = all;
      }

      let contactMap = {};
      if (contactRes.status === 'fulfilled' && contactRes.value.ok) {
        const d = await contactRes.value.json();
        const list = d.data || d || [];
        list.forEach(c => { contactMap[c.phone] = c.name || c.phone; });
      }
      setMessages(msgs);
      setWaContacts(contactMap);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── Enrich ── */
  const enriched = messages.map(m => ({
    ...m,
    contactName: m._contactName || waContacts[m.to] || m.to || '—',
    phone: m._wa_id || m.to || '—',
    templateName: extractTemplateName(m.body),
  }));

  /* ── Filters ── */
  const filtered = enriched.filter(m => {
    if (!m.timestamp) return false;
    const ds = new Date(parseInt(m.timestamp) * 1000).toISOString().slice(0, 10);
    if (dateFrom && ds < dateFrom) return false;
    if (dateTo   && ds > dateTo)   return false;
    if (filterStatus !== 'all' && m.status !== filterStatus) return false;
    if (searchName && !m.contactName.toLowerCase().includes(searchName.toLowerCase()) && !m.phone.includes(searchName)) return false;
    return true;
  });

  /* ── Totals ── */
  const totals = filtered.reduce((a, m) => {
    a.total++;
    if (m.status === 'read') a.read++;
    else if (m.status === 'delivered') a.delivered++;
    else if (m.status === 'failed') a.failed++;
    else a.sent++;
    return a;
  }, { total: 0, sent: 0, delivered: 0, read: 0, failed: 0 });

  /* ── Groups ── */
  const groups = {};
  filtered.forEach(m => {
    const dateKey = new Date(parseInt(m.timestamp) * 1000).toISOString().slice(0, 10);
    const key = `${m.templateName}__${dateKey}`;
    if (!groups[key]) groups[key] = { key, templateName: m.templateName, date: dateKey, sent: 0, delivered: 0, read: 0, failed: 0, total: 0, messages: [] };
    groups[key].total++;
    groups[key].messages.push(m);
    if (m.status === 'read') groups[key].read++;
    else if (m.status === 'delivered') groups[key].delivered++;
    else if (m.status === 'failed') groups[key].failed++;
    else groups[key].sent++;
  });
  const groupList = Object.values(groups).sort((a, b) => b.date.localeCompare(a.date));

  /* ── Retry helpers ── */
  const doRetry = async (msgs, label) => {
    if (!msgs.length || !window.confirm(`Retry ${msgs.length} failed message(s)?`)) return;
    setRetrying(true);
    let ok = 0, err = 0;
    for (const msg of msgs) {
      try {
        const r = await fetch(`${API}/send-template`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: msg.phone, templateName: msg.templateName, parameters: [] }),
        });
        if (r.ok) ok++; else err++;
      } catch { err++; }
    }
    setRetrying(false);
    alert(`✅ Retried ${ok}${err > 0 ? ` · ❌ ${err} failed` : ''}`);
    fetchAll();
  };

  const failedMsgs = filtered.filter(m => m.status === 'failed');
  const deliveryRate = pct(totals.delivered + totals.read, totals.total);

  return (
    <div className="page-wrapper" style={{ background: '#f8fafc' }}>
      <div className="content container-fluid" style={{ paddingBottom: 24 }}>

        {/* ── Page Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24, paddingTop: 8 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="fas fa-chart-bar" style={{ color: '#fff', fontSize: 17 }}></i>
              </div>
              <div>
                <h5 style={{ margin: 0, fontWeight: 800, color: '#0f172a', fontSize: 20 }}>Broadcast Report</h5>
                <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Track delivery, read rates and failures</p>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {failedMsgs.length > 0 && (
              <button onClick={() => doRetry(failedMsgs)} disabled={retrying}
                className="btn btn-sm"
                style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', fontWeight: 600, borderRadius: 8, padding: '7px 16px' }}>
                <i className="fas fa-redo me-1" style={{ fontSize: 12 }}></i>
                {retrying ? 'Retrying…' : `Retry All Failed (${failedMsgs.length})`}
              </button>
            )}
            <button onClick={fetchAll} disabled={loading}
              className="btn btn-sm"
              style={{ background: '#fff', color: '#374151', border: '1px solid #e2e8f0', fontWeight: 600, borderRadius: 8, padding: '7px 16px' }}>
              <i className={`fas fa-sync-alt me-1 ${loading ? 'fa-spin' : ''}`} style={{ fontSize: 12 }}></i>
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* ── Error ── */}
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 20, color: '#ef4444', fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <i className="fas fa-exclamation-triangle" style={{ marginTop: 2 }}></i>
            <span>{error}</span>
          </div>
        )}

        {/* ── Filters ── */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '16px 20px', marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>
          <div className="row g-2 align-items-end">
            <div className="col-md-3 col-sm-6">
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4, display: 'block' }}>From Date</label>
              <input type="date" className="form-control form-control-sm" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
            </div>
            <div className="col-md-3 col-sm-6">
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4, display: 'block' }}>To Date</label>
              <input type="date" className="form-control form-control-sm" value={dateTo} onChange={e => setDateTo(e.target.value)}
                style={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
            </div>
            <div className="col-md-2 col-sm-4">
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4, display: 'block' }}>Status</label>
              <select className="form-select form-select-sm" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                style={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}>
                <option value="all">All Statuses</option>
                <option value="sent">Sent</option>
                <option value="delivered">Delivered</option>
                <option value="read">Read</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="col-md-3 col-sm-8">
              <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4, display: 'block' }}>Search</label>
              <div style={{ position: 'relative' }}>
                <i className="fas fa-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 12 }}></i>
                <input type="text" className="form-control form-control-sm" placeholder="Name or number…" value={searchName} onChange={e => setSearchName(e.target.value)}
                  style={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, paddingLeft: 30 }} />
              </div>
            </div>
            <div className="col-md-1 col-sm-12">
              <button className="btn btn-sm w-100"
                style={{ borderRadius: 8, background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', fontSize: 13 }}
                onClick={() => { setDateFrom(''); setDateTo(''); setFilterStatus('all'); setSearchName(''); }}>
                Clear
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#94a3b8' }}>
            <div style={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
            <p style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>Loading broadcast data…</p>
          </div>
        ) : (
          <>
            {/* ── Stats row ── */}
            <div className="row g-3 mb-4">
              <div className="col-xl col-md-4 col-sm-6">
                <StatCard label="Total Sent" value={totals.total} color="#6366f1" bg="#f5f3ff" icon="fa-paper-plane"
                  sub={`${groupList.length} broadcast${groupList.length !== 1 ? 's' : ''}`} />
              </div>
              <div className="col-xl col-md-4 col-sm-6">
                <StatCard label="Delivered" value={totals.delivered} color="#10b981" bg="#f0fdf4" icon="fa-check-double"
                  pctVal={pct(totals.delivered, totals.total)} sub="of total sent" />
              </div>
              <div className="col-xl col-md-4 col-sm-6">
                <StatCard label="Read" value={totals.read} color="#8b5cf6" bg="#f5f3ff" icon="fa-eye"
                  pctVal={pct(totals.read, totals.total)} sub="opened message" />
              </div>
              <div className="col-xl col-md-4 col-sm-6">
                <StatCard label="Failed" value={totals.failed} color="#ef4444" bg="#fef2f2" icon="fa-times-circle"
                  pctVal={pct(totals.failed, totals.total)} sub={totals.failed > 0 ? 'Need retry' : 'All good!'} />
              </div>
              <div className="col-xl col-md-4 col-sm-6">
                <StatCard label="Pending" value={totals.sent} color="#f59e0b" bg="#fffbeb" icon="fa-clock"
                  pctVal={pct(totals.sent, totals.total)} sub="awaiting update" />
              </div>
            </div>

            {/* ── Analytics row ── */}
            <div className="row g-3 mb-4">
              {/* Delivery funnel */}
              <div className="col-lg-7">
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '24px', height: '100%', boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>
                  <h6 style={{ margin: '0 0 20px', fontWeight: 700, color: '#0f172a', fontSize: 15 }}>
                    <i className="fas fa-filter me-2" style={{ color: '#6366f1' }}></i>
                    Delivery Funnel
                  </h6>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <FunnelRow label="Sent"              value={totals.total}                   total={totals.total} color="#6366f1" icon="fa-paper-plane" />
                    <FunnelRow label="Delivered or Read" value={totals.delivered + totals.read} total={totals.total} color="#10b981" icon="fa-check-double" />
                    <FunnelRow label="Read"              value={totals.read}                    total={totals.total} color="#8b5cf6" icon="fa-eye" />
                    <FunnelRow label="Failed"            value={totals.failed}                  total={totals.total} color="#ef4444" icon="fa-times-circle" />
                  </div>
                </div>
              </div>

              {/* Delivery rate gauge */}
              <div className="col-lg-5">
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '24px', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>
                  <h6 style={{ margin: '0 0 20px', fontWeight: 700, color: '#0f172a', fontSize: 15, alignSelf: 'flex-start' }}>
                    <i className="fas fa-tachometer-alt me-2" style={{ color: '#6366f1' }}></i>
                    Overall Delivery Rate
                  </h6>

                  {/* Ring */}
                  <div style={{ position: 'relative', width: 140, height: 140, marginBottom: 20 }}>
                    <svg width="140" height="140" viewBox="0 0 140 140">
                      <circle cx="70" cy="70" r="58" fill="none" stroke="#f1f5f9" strokeWidth="14" />
                      <circle cx="70" cy="70" r="58" fill="none"
                        stroke={deliveryRate >= 70 ? '#10b981' : deliveryRate >= 40 ? '#f59e0b' : '#ef4444'}
                        strokeWidth="14" strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 58}`}
                        strokeDashoffset={`${2 * Math.PI * 58 * (1 - deliveryRate / 100)}`}
                        transform="rotate(-90 70 70)"
                        style={{ transition: 'stroke-dashoffset .8s ease' }}
                      />
                    </svg>
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 30, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{deliveryRate}%</span>
                      <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>delivered</span>
                    </div>
                  </div>

                  {/* Mini legend */}
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {[
                      { color: '#10b981', label: 'Delivered', val: totals.delivered },
                      { color: '#8b5cf6', label: 'Read',      val: totals.read },
                      { color: '#ef4444', label: 'Failed',    val: totals.failed },
                    ].map(item => (
                      <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: item.color }}></div>
                        <span style={{ fontSize: 12, color: '#64748b' }}>{item.label} <b style={{ color: '#374151' }}>{item.val}</b></span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Tabs ── */}
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>

              {/* Tab nav */}
              <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', padding: '0 4px' }}>
                {[
                  { key: 'history', icon: 'fa-layer-group',   label: 'Broadcast History' },
                  { key: 'detail',  icon: 'fa-list-ul',       label: 'Detailed Log' },
                ].map(tab => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                    style={{
                      background: 'none', border: 'none', padding: '14px 22px', cursor: 'pointer',
                      fontWeight: activeTab === tab.key ? 700 : 500, fontSize: 14,
                      color: activeTab === tab.key ? '#6366f1' : '#64748b',
                      borderBottom: activeTab === tab.key ? '2px solid #6366f1' : '2px solid transparent',
                      display: 'flex', alignItems: 'center', gap: 8, transition: 'all .2s',
                    }}>
                    <i className={`fas ${tab.icon}`} style={{ fontSize: 13 }}></i>
                    {tab.label}
                    <span style={{
                      background: activeTab === tab.key ? '#ede9fe' : '#f1f5f9',
                      color: activeTab === tab.key ? '#6366f1' : '#94a3b8',
                      borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 700,
                    }}>
                      {tab.key === 'history' ? groupList.length : filtered.length}
                    </span>
                  </button>
                ))}
              </div>

              {/* ══ HISTORY TAB ══ */}
              {activeTab === 'history' && (
                <div style={{ padding: 20 }}>
                  {groupList.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
                      <i className="fas fa-inbox" style={{ fontSize: 40, opacity: .3, display: 'block', marginBottom: 12 }}></i>
                      <p style={{ margin: 0 }}>No broadcast history found</p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {groupList.map((group, gi) => {
                        const dr = pct(group.delivered + group.read, group.total);
                        const isOpen = expandedGroup === group.key;
                        const grpFailed = group.messages.filter(m => m.status === 'failed');
                        const drColor = dr >= 70 ? '#10b981' : dr >= 40 ? '#f59e0b' : '#ef4444';

                        return (
                          <div key={group.key} style={{
                            border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden',
                            boxShadow: isOpen ? '0 4px 12px rgba(99,102,241,.08)' : 'none',
                            transition: 'box-shadow .2s',
                          }}>
                            {/* Group header */}
                            <div onClick={() => setExpandedGroup(isOpen ? null : group.key)}
                              style={{
                                padding: '16px 20px', cursor: 'pointer',
                                background: isOpen ? '#fafbff' : '#fff',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                flexWrap: 'wrap', gap: 12,
                                borderLeft: `4px solid ${drColor}`,
                                transition: 'background .2s',
                              }}>

                              {/* Left: info */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                                <div style={{ width: 40, height: 40, borderRadius: 10, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <i className="fas fa-layer-group" style={{ color: '#6366f1', fontSize: 16 }}></i>
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {group.templateName}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <i className="fas fa-calendar-alt"></i>
                                    {fmtDate(group.messages[0]?.timestamp)}
                                    <span>·</span>
                                    <i className="fas fa-users"></i>
                                    {group.total} recipients
                                  </div>
                                </div>

                                {/* Mini stat pills */}
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  {[
                                    { label: 'Total',     val: group.total,     color: '#6366f1', bg: '#ede9fe' },
                                    { label: 'Delivered', val: group.delivered, color: '#10b981', bg: '#f0fdf4' },
                                    { label: 'Read',      val: group.read,      color: '#8b5cf6', bg: '#f5f3ff' },
                                    { label: 'Failed',    val: group.failed,    color: '#ef4444', bg: '#fef2f2' },
                                    { label: 'Pending',   val: group.sent,      color: '#f59e0b', bg: '#fffbeb' },
                                  ].map(s => (
                                    <div key={s.label} style={{
                                      background: s.bg, borderRadius: 8,
                                      padding: '5px 12px', textAlign: 'center', minWidth: 52,
                                    }}>
                                      <div style={{ fontSize: 15, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.val}</div>
                                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{s.label}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Right: rate + actions */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ fontSize: 22, fontWeight: 800, color: drColor, lineHeight: 1 }}>{dr}%</div>
                                  <div style={{ fontSize: 10, color: '#94a3b8' }}>delivery</div>
                                </div>
                                {grpFailed.length > 0 && (
                                  <button onClick={e => { e.stopPropagation(); doRetry(grpFailed); }}
                                    style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                    <i className="fas fa-redo me-1" style={{ fontSize: 10 }}></i>
                                    Retry {grpFailed.length}
                                  </button>
                                )}
                                <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ color: '#94a3b8', fontSize: 13 }}></i>
                              </div>
                            </div>

                            {/* Expanded rows */}
                            {isOpen && (
                              <div style={{ borderTop: '1px solid #e2e8f0', overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ background: '#f8fafc' }}>
                                      {['#', 'Contact', 'Phone', 'Sent At', 'Status', ''].map(h => (
                                        <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: .5, whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {[...group.messages]
                                      .sort((a, b) => parseInt(a.timestamp) - parseInt(b.timestamp))
                                      .map((msg, i) => (
                                        <tr key={msg.id || i}
                                          style={{ borderBottom: '1px solid #f1f5f9', transition: 'background .15s' }}
                                          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                          <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13 }}>{i + 1}</td>
                                          <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{msg.contactName}</td>
                                          <td style={{ padding: '12px 16px', color: '#64748b', fontFamily: 'monospace', fontSize: 13 }}>{msg.phone}</td>
                                          <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>{fmtTime(msg.timestamp)}</td>
                                          <td style={{ padding: '12px 16px' }}><StatusBadge status={msg.status} /></td>
                                          <td style={{ padding: '12px 16px' }}>
                                            {msg.status === 'failed' && (
                                              <button onClick={() => doRetry([msg])}
                                                style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                                <i className="fas fa-redo me-1" style={{ fontSize: 10 }}></i>Retry
                                              </button>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ══ DETAIL TAB ══ */}
              {activeTab === 'detail' && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        {['#', 'Contact', 'Phone', 'Template', 'Sent At', 'Status', ''].map(h => (
                          <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: .5, whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr><td colSpan={7} style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
                          <i className="fas fa-inbox" style={{ fontSize: 32, display: 'block', marginBottom: 10, opacity: .3 }}></i>
                          No messages found
                        </td></tr>
                      ) : [...filtered]
                        .sort((a, b) => parseInt(b.timestamp) - parseInt(a.timestamp))
                        .map((msg, i) => (
                          <tr key={msg.id || i}
                            style={{ borderBottom: '1px solid #f1f5f9', transition: 'background .15s' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13 }}>{i + 1}</td>
                            <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0f172a', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.contactName}</td>
                            <td style={{ padding: '12px 16px', color: '#64748b', fontFamily: 'monospace', fontSize: 13 }}>{msg.phone}</td>
                            <td style={{ padding: '12px 16px', fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <span style={{ background: '#ede9fe', color: '#6366f1', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
                                {msg.templateName}
                              </span>
                            </td>
                            <td style={{ padding: '12px 16px', color: '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>{fmtTime(msg.timestamp)}</td>
                            <td style={{ padding: '12px 16px' }}><StatusBadge status={msg.status} /></td>
                            <td style={{ padding: '12px 16px' }}>
                              {msg.status === 'failed' && (
                                <button onClick={() => doRetry([msg])}
                                  style={{ background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                  <i className="fas fa-redo me-1" style={{ fontSize: 10 }}></i>Retry
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>

                  {/* Footer */}
                  <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc' }}>
                    <span style={{ fontSize: 13, color: '#64748b' }}>
                      Showing <b>{filtered.length}</b> of <b>{messages.length}</b> messages
                      {filterStatus !== 'all' && <> · <span style={{ color: '#6366f1' }}>{filterStatus}</span></>}
                      {searchName && <> · search: <span style={{ color: '#6366f1' }}>"{searchName}"</span></>}
                    </span>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>
                      Delivery rate: <b style={{ color: deliveryRate >= 70 ? '#10b981' : deliveryRate >= 40 ? '#f59e0b' : '#ef4444' }}>{deliveryRate}%</b>
                    </span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
